import React, { useState } from 'react';
import { useWebsiteConfiguration } from './hooks/useWebsiteConfiguration';
import { Loading } from '../../healtheraComponents';
import PlusWebsiteEditor from './PlusWebsiteEditor';
import { StyledLoadingContainer } from './PlusWebsiteEditor.styles';
import { createEmptyConfig } from './utils/config';
import { Organisation } from '../../types/organisation';
import { ApiReadyWebsiteConfiguration, WebsiteConfiguration } from './types/websiteConfiguration';
import { coreApi } from '../../utils/axios';
import { useWebsiteSections } from './hooks/useWebsiteSections';
import { StyledErrorText } from './components/ImageUploader/ImageUploader.styles';
import NotificationPopup from '../NotificationPopup';
import errorRoutine from '../../utils/errorRoutine';
import { prepareForRender } from './utils/format';
import { usePostalPharmaciesCount } from './hooks/usePostalPharmaciesCount';
import { getValidationSchema } from './utils/validationSchema';
import { EditorLayout } from './types/editor';

type Props = {
  organisation: Organisation;
};

const PlusWebsiteEditorContainer = ({ organisation }: Props) => {
  const { isLoaded: isConfigLoaded, isLoading: isConfigLoading, config, setConfig } = useWebsiteConfiguration(organisation._id);
  const { websiteSections, isLoading: isWebsiteSectionsLoading, error: websiteSectionsError } = useWebsiteSections(organisation._id);
  const { postalPharmaciesCount, isLoading: isLoadingPostalPharmaciesCount, error: postalPharmaciesCountError } = usePostalPharmaciesCount(organisation._id);
  const [notification, setNotification] = useState<string>();

  const isNewConfig = !config;

  const handleSubmit = async (values: ApiReadyWebsiteConfiguration) => {
    try {      
      if (isNewConfig) {
        const response = await coreApi.post<WebsiteConfiguration>(`chains/${organisation._id}/website-settings`, values);
        setNotification('Successfully created!');
        setConfig(prepareForRender(response.data));
      } else {
        await coreApi.put(`chains/${organisation._id}/website-settings`, values);
        setNotification('Successfully saved!');
      }
    } catch (err) {
      errorRoutine(err, (message) => setNotification(message));
    }
  };

  if (websiteSectionsError || postalPharmaciesCountError) {
    return (
      <StyledLoadingContainer>
        <StyledErrorText>{websiteSectionsError || postalPharmaciesCountError}</StyledErrorText>
      </StyledLoadingContainer>
    );
  }

  if ((!isConfigLoaded && isConfigLoading) || isLoadingPostalPharmaciesCount || isWebsiteSectionsLoading || !websiteSections) {
    return (
      <StyledLoadingContainer>
        <Loading />
      </StyledLoadingContainer>
    );
  }

  return (
    <>
      <NotificationPopup isOpen={!!notification} onClose={() => setNotification(undefined)} message={notification} />
      <PlusWebsiteEditor
        layout={organisation.type === 'prescriber' ? EditorLayout.PRESCRIBER : EditorLayout.PHARMACY}
        validationSchema={getValidationSchema(organisation.type === 'prescriber')}
        postalPharmaciesCount={postalPharmaciesCount}
        availableSections={websiteSections}
        isNewConfig={isNewConfig}
        config={config || createEmptyConfig(organisation)}
        onSubmit={handleSubmit}
        setNotification={setNotification}
      />
    </>
  );
};

export default PlusWebsiteEditorContainer;
